import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtDate } from "@/lib/radar";
import { toast } from "sonner";
import { SlidersHorizontal, PlayCircle, CheckCircle2, XCircle, History } from "lucide-react";

export default function ScoringAdmin() {
  const [cfg, setCfg] = useState<any>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [tests, setTests] = useState<any>(null);
  const [form, setForm] = useState<any>({ p1: 80, p2: 65, p3: 50, p4: 30, w0: 30, w1: 90, w2: 180, cap: 0.5, confMin: 70 });

  const load = () => {
    api.get("/scoring/config").then((r) => {
      setCfg(r.data);
      const c = r.data.config;
      setForm({
        p1: c.thresholds.p1, p2: c.thresholds.p2, p3: c.thresholds.p3, p4: c.thresholds.p4,
        w0: c.recency_windows[0], w1: c.recency_windows[1], w2: c.recency_windows[2],
        cap: c.correlation_cap, confMin: c.confidence_p1_min,
      });
    });
    api.get("/scoring/versions").then((r) => setVersions(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const config = {
      thresholds: { p1: +form.p1, p2: +form.p2, p3: +form.p3, p4: +form.p4 },
      recency_windows: [+form.w0, +form.w1, +form.w2],
      correlation_cap: +form.cap, confidence_p1_min: +form.confMin,
    };
    try {
      await api.put("/scoring/config", { config, notes: "Admin threshold/window/cap update" });
      toast.success("Config saved — all companies re-scored under new model version");
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const runTests = async () => {
    const { data } = await api.post("/scoring/run-tests");
    setTests(data);
    toast[data.passed ? "success" : "error"](data.passed ? "All deterministic tests passed" : "Some tests failed");
  };

  if (!cfg) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="scoring-admin-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-teal-700" /> Scoring Administration</h1>
        <p className="text-sm text-slate-500">Active model <span className="num font-semibold">{cfg.active_version}</span>. Signal weights are fixed per model version; thresholds, recency windows and the correlation cap are configurable.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 border-slate-200 shadow-none" data-testid="config-form">
          <h3 className="font-heading font-semibold text-slate-800 mb-3">Configurable Rules</h3>
          <div className="grid grid-cols-2 gap-3">
            {[["p1", "P1 threshold"], ["p2", "P2 threshold"], ["p3", "P3 threshold"], ["p4", "P4 threshold"],
              ["w0", "Recency 100% ≤ days"], ["w1", "Recency 75% ≤ days"], ["w2", "Recency 50% ≤ days"], ["confMin", "P1 min confidence"]].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-xs">{lbl}</Label>
                <Input type="number" data-testid={`cfg-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="num" />
              </div>
            ))}
            <div className="col-span-2">
              <Label className="text-xs">Correlation cap (fraction of DI/WN overlap retained, 0–1)</Label>
              <Input type="number" step="0.05" min="0" max="1" data-testid="cfg-cap" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="num" />
            </div>
          </div>
          <Button className="mt-4 bg-[#0B1C3D] hover:bg-[#122a55]" onClick={save} data-testid="save-config">Save & re-score all</Button>
        </Card>

        <Card className="p-4 border-slate-200 shadow-none" data-testid="deterministic-tests">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-slate-800">Deterministic Test Cases</h3>
            <Button size="sm" variant="outline" onClick={runTests} data-testid="run-tests"><PlayCircle className="h-4 w-4 mr-1" /> Run tests</Button>
          </div>
          {!tests && <div className="text-sm text-slate-400">Run the built-in guardrail tests (determinism, correlation cap, absence-of-evidence, P1 gating).</div>}
          {tests && (
            <div className="space-y-2">
              <div className={`text-sm font-semibold ${tests.passed ? "text-emerald-700" : "text-red-600"}`}>
                {tests.passed ? "All tests passed" : "Failures detected"}
              </div>
              {tests.cases.map((c: any, i: number) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  {c.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-600 mt-0.5" />}
                  <div>
                    <div className="text-slate-700">{c.name}</div>
                    <div className="text-[11px] text-slate-400 num">{JSON.stringify(c.detail)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-0 border-slate-200 shadow-none overflow-hidden" data-testid="version-history">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2"><History className="h-4 w-4" /><h3 className="font-heading font-semibold text-slate-800">Model Version History</h3></div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="text-left font-medium px-4 py-2">Version</th><th className="text-left font-medium px-3 py-2">Active</th>
            <th className="text-left font-medium px-3 py-2">Notes</th><th className="text-right font-medium px-3 py-2">Created</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {versions.map((v) => (
              <tr key={v.id}>
                <td className="px-4 py-2 num font-medium text-slate-800">{v.version}</td>
                <td className="px-3 py-2">{v.is_active ? <span className="text-emerald-700 text-xs font-semibold">Active</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                <td className="px-3 py-2 text-slate-600">{v.notes}</td>
                <td className="px-3 py-2 text-right num text-[11px] text-slate-400">{fmtDate(v.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
